<template>
  <div class="custom-card card">
    <div class="bg-surface-0 dark:bg-surface-900">
      <div class="flex flex-wrap gap-4 items-center mb-4 md:flex-nowrap">
        <Calendar v-model="searchForm.fromDate" dateFormat="yy-mm-dd" placeholder="시작일" showIcon class="w-40 sm:w-12rem min-w-[10rem]" />
        <span>~</span>
        <Calendar v-model="searchForm.toDate" dateFormat="yy-mm-dd" placeholder="종료일" showIcon class="w-40 sm:w-12rem min-w-[10rem]" />
        <Dropdown v-model="searchForm.status" :options="statusOptions" optionLabel="name" optionValue="value" placeholder="상태 선택" class="sm:w-1rem" />
        <Button label="검색" icon="pi pi-search" @click="searchIssues" class="min-w-[80px]"/>
        <Button label="전체" icon="pi pi-list" @click="resetSearch" class="p-button-icon min-w-[80px]" />
        <Button label="고장신고요청" icon="pi pi-plus" @click="openNewReportDialog"/>
      </div>
    </div>

    <DataTable :value="issues" :paginator="true" :rows="10" 
               selectionMode="single" @row-select="onRowSelect"
               :loading="loading" class="p-datatable-sm"
               :scrollable="true"
               scrollHeight="calc(100vh - 340px)"
               showGridlines
               stripedRows>
      <Column field="id" header="고장신고ID" sortable/>
      <Column class="mobile-hide" field="itemMinorName" header="자산분류" sortable />
      <Column class="mobile-hide" field="assetNumber" header="자산번호" sortable />
      <Column field="reporterName" header="신고자" sortable />
      <Column class="mobile-hide w-3/12" field="description" header="신고내용" sortable />
      <Column field="created_at" header="신고 일시" sortable>
        <template #body="slotProps">
          {{ formatDate(slotProps.data.created_at) }}
        </template>
      </Column>
      <Column field="status" header="처리 상태">
        <template #body="slotProps">
          <Tag :value="getStatusLabel(slotProps.data.status)" :severity="getStatusSeverity(slotProps.data.status)" />
        </template>
      </Column>
      <Column field="handled_at" header="처리 일시" sortable>
        <template #body="slotProps">
          {{ formatDate(slotProps.data.handled_at) }}
        </template>
      </Column>
    </DataTable>

    <IssueReportDialog 
      v-if="showReportDialog" 
      :issue-id="selectedIssueId" 
      :is-view-mode="isViewModeDialog"
      @close="closeReportDialog" 
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useIssueStore } from '@/stores/issue';
import { ISSUE_STATUS_OPTIONS, ISSUE_STATUS_SEVERITY } from '@/constants/issueStatus';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import IssueReportDialog from '@/components/IssueReportDialog.vue';

const issueStore = useIssueStore();
const loading = ref(false);
const issues = ref([]);
const showReportDialog = ref(false);
const selectedIssueId = ref(null);
const isViewModeDialog = ref(true);

const searchForm = ref({
  fromDate: null,
  toDate: null,
  status: null
});

const statusOptions = ISSUE_STATUS_OPTIONS;

const formatDate = (date) => {
  if (date) {
    return new Date(date).toLocaleString();
  } else {
    return '';
  }
};

const getStatusLabel = (status) => {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.name : status;
};

const getStatusSeverity = (status) => {
  return ISSUE_STATUS_SEVERITY[status] || 'info';
};

const searchIssues = async () => {
  loading.value = true;
  try {
    const response = await issueStore.searchIssues(searchForm.value);
    issues.value = response.items;
  } catch (error) {
    console.error('Error fetching issues:', error);
  } finally {
    loading.value = false;
  }
};

const resetSearch = () => {
  searchForm.value = {
    fromDate: null,
    toDate: null,
    status: null
  };
  searchIssues();
};

const openNewReportDialog = () => {
  selectedIssueId.value = 0;
  isViewModeDialog.value = false;
  showReportDialog.value = true;
};

const onRowSelect = (event) => {
  selectedIssueId.value = event.data.id;
  isViewModeDialog.value = true;
  showReportDialog.value = true;
};

const closeReportDialog = () => {
  showReportDialog.value = false;
  selectedIssueId.value = null;
};

onMounted(() => {
  searchIssues();
});
</script>

<style scoped>
.custom-card {
  padding: 0.5rem 1.5rem 1.5rem 1.5rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  @apply bg-surface-100 dark:bg-surface-800 font-semibold;
  @apply text-surface-700 dark:text-surface-0;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  @apply text-surface-700 dark:text-surface-0;
  @apply border-surface-200 dark:border-surface-700;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  @apply bg-surface-100 dark:bg-surface-800/60;
}

:deep(.p-dropdown .p-inputtext),
:deep(.p-calendar .p-inputtext) {
  @apply text-sm;
}

/* 모바일에서 특정 컬럼 숨기기 */
@media (max-width: 768px) {
  :deep(.mobile-hide) {
    display: none !important;
  }

  :deep(.asset-number-column) {
    font-size: 0.75rem; /* text-xs와 동일한 크기 */
  }
}
</style>